import React from 'react';
import {withRouter} from '../../core/withRouter';
import {observer} from 'mobx-react';
import {makeObservable} from 'mobx';
import {PropTypes} from 'prop-types';
import {history} from 'fn-common-ui';
import {a11yAnnounce} from 'fn-common-ui';

@observer
class NewAppProgressBar extends React.Component {

	static propTypes = {
		store: PropTypes.object.isRequired,
		match: PropTypes.object.isRequired,
		disabled: PropTypes.bool
	}

	static defaultProps = {
		disabled: false
	}

	constructor(props) {
		super(props);
		makeObservable(this);
		this.store = this.props.store;
		this.progressStep1 = React.createRef();
		this.progressStep2 = React.createRef();
		this.progressStep3 = React.createRef();
		this.progressStep4 = React.createRef();
		this.progressStep5 = React.createRef();

		this.progressStepRefs = {
			'app-details': this.progressStep1,
			'app-package': this.progressStep2,
			'images': this.progressStep3,
			'review': this.progressStep4,
			'submit': this.progressStep5
		}

		this.steps = [
			{
				number: '1',
				id: 'app-details',
				name: 'App Details',
				errorMapping: 'newAppDetailsHasErrors'
			},
			{
				number: '2',
				id: 'app-package',
				name: 'App Package',
				errorMapping: 'newAppPackageUploadHasErrors'
			},
			{
				number: '3',
				id: 'app-screenshots',
				name: 'Images',
				errorMapping: 'newAppScreenshotUploadHasErrors'
			},
			{
				number: '4',
				id: 'app-review',
				name: 'Review'
			},
			{
				number: '5',
				id: 'app-submit',
				name: 'Submit'
			}
		]
	}

	componentDidMount(){
 }
  

	get currentStepId() {
		return this.props.match.params.section;
	}

	get formIsValid() {
		this.steps.forEach(step => {
			if(this.stepHasErrors(step.errorMapping)){
				return false;
			}
		});
		return true;
	}

	get barIsDisabled() {
		return this.props.disabled || !this.store.newAppData.saveId || this.store.masterStateProps.namePlatformIsEditable || this.store.masterStateProps.isSavingAndChecking;
	}

	get reviewStepsAreEnabled() {
		return this.store.newAppData.saveId && this.store.saveAndCheckHasBeenClicked && this.formIsValid;
	}

	stepHasErrors = (errorMapping) => {
		if(errorMapping !== undefined) {
			return this.store.newAppValidationsStore[errorMapping];
		}
		return true;
	}

	stepIsEnabled = (stepId) => {
		if(this.barIsDisabled) {
			return false;
		} else if(stepId === 'app-review') {
			return this.reviewStepsAreEnabled;
		} else if(stepId === 'app-submit') {
			return this.reviewStepsAreEnabled && this.currentStepId === 'app-submit';
		} else {
			return true;
		}
	}

	getStepObject = (stepId) => {
		return this.steps.filter(step => stepId === step.id)[0];
	}

	isReviewStep = (stepId) => {
		return stepId === 'app-review' || stepId === 'app-submit';
	}

	getStepClasses = (stepId) => {
		const stepObject = this.getStepObject(stepId);
		let classNames = '';

		if(this.stepIsEnabled(stepId)) {
			classNames += ' enabled';
		} else {
			// Disabled is default. If disabled, do not append any other classes.
			return '';
		}

		if(this.currentStepId === stepId){
			classNames += ' current';
		}

		if(this.store.masterStateProps.errorsAreDisplayed || this.isReviewStep(this.currentStepId)){
			if(!this.isReviewStep(stepId)) {
				if(this.stepHasErrors(stepObject.errorMapping)) {
					classNames += ' has-errors';
				} else {
					classNames += ' is-complete-and-valid';
				}
			} else if (this.currentStepId === 'app-submit' && stepId === 'app-review') {
				classNames += ' is-complete-and-valid';
			}
		}

		return classNames;
	}

	onChangeSection = (e, stepId) => {
		e.preventDefault();
		if(this.stepIsEnabled(stepId)) {
			history.push(`/new-application/${stepId}/${this.store.newAppData.saveId}`);
		} else if(this.store.masterStateProps.namePlatformIsEditable) {
			a11yAnnounce({
				message: 'Please enter an app name and select platform to proceed.',
				messageType: 'status'
			});
		} else {
			a11yAnnounce({
				message: 'Please complete the form and resolve any errors to proceed.',
				messageType: 'status'
			});
		}
	};

	sectionLineIndicator = () => {
		if(this.store.newAppData.saveId) {
			return this.reviewStepsAreEnabled ? 'app-submit' : 'app-screenshots';
		}
		return '';
	}

	renderProgressBubble(stepId) {
		const stepObject = this.getStepObject(stepId);
		const isCurrentlyEditing = stepId === this.currentStepId && !this.barIsDisabled;
		const ref = this.progressStepRefs[stepId];
		console.log('ref', ref);
		const jsxInTheCircle = isCurrentlyEditing
			? <div><i className="icon icon-pencil white" aria-hidden="true"></i></div>
			: <div>{stepObject.number}</div>

		return (
			<a id={stepId} href="" onClick={e => {this.onChangeSection(e, stepId)}} aria-disabled={!this.stepIsEnabled(stepId)} className={this.getStepClasses(stepId)} ref={ref}>
				<span className="sr-only">{isCurrentlyEditing ? 'Currently Editing.' : ''}</span>
				<span className="sr-only">Step</span>
				<span className="app-progress-step-icon">
					<div>{jsxInTheCircle}</div>
				</span>
				<p>{stepObject.name}</p>
			</a>
		)
	}

	render() {
		return (
			<section className={`app-progress-bar walkthrough-submission-process ${this.sectionLineIndicator()} ${this.barIsDisabled ? 'off' : ''}`}>
				<div className="container">
					<ul className="row">
						<li className="col-2">
							{this.renderProgressBubble('app-details')}
						</li>
						<li className="col-3">
							{this.renderProgressBubble('app-package')}
						</li>
						<li className="col-2">
							{this.renderProgressBubble('app-screenshots')}
						</li>
						<li className="col-3">
							{this.renderProgressBubble('app-review')}
						</li>
						<li className="col-2">
							{this.renderProgressBubble('app-submit')}
						</li>
					</ul>
				</div>
			</section>
		);
	}
}

export default withRouter(NewAppProgressBar);
